import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';

export type UnitOverlayData = {
  unitId: string;
  colorOverlay?: string;
  material?: string;
  materialOverlay?: string;
};

interface BuildingModelContextProps {
  selectUnit(unitId: string | null): void;
  selectedUnitId: string | null;
  hoveredUnitId: string | null;
  setHoveredUnitId(unitId: string | null): void;
  unitsOverlayData: Record<string, UnitOverlayData>;
  setUnitOverlayData(data: UnitOverlayData): void;
}

const BuildingModelContext = createContext<
  BuildingModelContextProps | undefined
>(undefined);

export const useBuildingModel = () => {
  const context = useContext(BuildingModelContext);
  if (!context) {
    throw new Error(
      'useBuildingModel must be used within an BuildingModelProvider'
    );
  }
  return context;
};

interface BuildingModelProviderProps {
  children: ReactNode;
}

export const BuildingModelProvider: React.FC<BuildingModelProviderProps> = ({
  children,
}) => {
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  const [hoveredUnitId, setHoveredUnitId] = useState<string | null>(null);
  const [unitsOverlayData, setUnitsOverlayData] = useState<
    Record<string, UnitOverlayData>
  >({});

  const selectUnit = useCallback((unitId: string | null) => {
    setSelectedUnitId(unitId);
  }, []);

  const setUnitOverlayData = useCallback((data: UnitOverlayData) => {
    setUnitsOverlayData((prevData) => {
      const existingData = prevData[data.unitId];

      // Compare the existing data with the new data to avoid unnecessary updates
      if (
        existingData &&
        existingData.colorOverlay === data.colorOverlay &&
        existingData.material === data.material &&
        existingData.materialOverlay === data.materialOverlay
      ) {
        // If data hasn't changed, avoid setting state to prevent re-renders
        return prevData;
      }

      // Otherwise, update the state immutably
      return {
        ...prevData,
        [data.unitId]: {
          ...existingData, // Preserve previous data
          ...data, // Merge new data for the unitId
        },
      };
    });
  }, []);

  const value = useMemo(
    () => ({
      selectUnit,
      selectedUnitId,
      hoveredUnitId,
      setHoveredUnitId,
      unitsOverlayData,
      setUnitOverlayData,
    }),
    [
      selectUnit,
      selectedUnitId,
      hoveredUnitId,
      setHoveredUnitId,
      unitsOverlayData,
      setUnitOverlayData,
    ]
  );

  return (
    <BuildingModelContext.Provider value={value}>
      {children}
    </BuildingModelContext.Provider>
  );
};
